import React, { useMemo, useState, useRef, useCallback, useContext } from 'react';
import { Box, Typography, Chip, Paper, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip as ChartTooltip,
  Filler,
} from 'chart.js';
import { RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
import { useDispatch, useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { downloadCspmReport, clearAllCspmMessages } from "../../../store/cspm/cspmSlice";
import { useSecurityScoreData } from "../../Dashboard/SecurityScore/useSecurityScoreData";
import { useAccessibleUserAccounts } from "../../../components/Common/HelperComponents/CustomHooks";
import { useOrganizations } from "../../../components/Common/HelperComponents/GetDataFromStore";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";
import { CloudProviderContext, getAccountCloudType } from "../../../CloudProviderContext";

const SEVERITY_COLORS = {
  CRITICAL: '#d32f2f',
  HIGH: '#f44336',
  MEDIUM: '#fbc02d',
  LOW: '#4caf50',
  INFORMATIONAL: '#1976d2',
};
const SEVERITY_BG_COLORS = {
  CRITICAL: '#ffebee',
  HIGH: '#ffebee',
  MEDIUM: '#fff9c4',
  LOW: '#e8f5e9',
  INFORMATIONAL: '#e3f2fd',
};
const SEVERITY_LABELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];

// Register Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ChartTooltip,
  Filler
);

const TREND_COLOR = "#1976d2";
const SUBTITLE_COLOR = "#6b7a90";

// Add percentage formatting helper
function formatPercent(val) {
  if (val == null || isNaN(val)) return '';
  const num = Number(val);
  if (Number.isInteger(num)) return `${num}%`;
  const fixed = num.toFixed(2);
  if (fixed.endsWith('00')) return `${num.toFixed(0)}%`;
  if (fixed.endsWith('0')) return `${num.toFixed(1)}%`;
  return `${fixed}%`;
}


const CSPMHeader = ({ scanObj, trendScore, isLoading = false }) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const {
    isDownloadingCspmReport,
    downloadCspmReportMessage,
    downloadCspmReportError,
  } = useSelector((state) => state.cspm);

  // Organization data hooks — only accCodes for the active cloud toggle (AWS or Azure)
  const { selectedProvider } = useContext(CloudProviderContext);
  const accessibleAccounts = useAccessibleUserAccounts();
  const organizations = useOrganizations();
  const accCodes = useMemo(
    () =>
      accessibleAccounts
        .filter((account) => getAccountCloudType(account) === selectedProvider)
        .map((account) => account.accountCode),
    [accessibleAccounts, selectedProvider]
  );
  const orgCode = organizations?.[0]?.orgcode;
  const { data: orgData, loading: orgLoading } = useSecurityScoreData(accCodes, orgCode);

  // Account view metrics
  const accountMetrics = useMemo(() => {
    return scanObj?.cspmMetricResult || {
      executedChecks: 0,
      passedChecks: 0,
      failedChecks: 0,
      percentChecks: 0,
      manual: 0,
      uniqueResources: 0,
      failedResources: 0,
      passedResources: 0,
    };
  }, [scanObj]);

  // Organization view metrics - transform Dashboard API data
  const orgMetrics = useMemo(() => {
    if (!orgData) {
      return {
        executedChecks: 0,
        passedChecks: 0,
        failedChecks: 0,
        percentChecks: 0,
        uniqueResources: 0,
        failedResources: 0,
        passedResources: 0,
        severityData: {
          CRITICAL: 0,
          HIGH: 0,
          MEDIUM: 0,
          LOW: 0,
          INFORMATIONAL: 0,
        },
      };
    }

    const totalFailed = 
      (orgData.securityPostCriticalTotal || 0) +
      (orgData.securityPostHighTotal || 0) +
      (orgData.securityPostModerateTotal || 0) +
      (orgData.securityPostLowTotal || 0);
    
    const totalPassed = (orgData.resourceTotalCount || 0) - (orgData.resourceFailedCount || 0);
    const totalChecks = totalFailed + totalPassed;

    return {
      executedChecks: totalChecks,
      passedChecks: totalPassed,
      failedChecks: totalFailed,
      percentChecks: parseFloat(orgData.securityPostSecPercent || 0),
      uniqueResources: orgData.resourceTotalCount || 0,
      failedResources: orgData.resourceFailedCount || 0,
      passedResources: totalPassed,
      severityData: {
        CRITICAL: orgData.securityPostCriticalTotal || 0,
        HIGH: orgData.securityPostHighTotal || 0,
        MEDIUM: orgData.securityPostModerateTotal || 0,
        LOW: orgData.securityPostLowTotal || 0,
        INFORMATIONAL: 0,
      },
    };
  }, [orgData]);

  // Trend chart data (last 30 days) - Format for Chart.js
  const trendChartData = useMemo(() => {
    if (!trendScore) return { labels: [], data: [] };
    const entries = Object.entries(trendScore).sort(
      ([a], [b]) => new Date(a) - new Date(b)
    );
    const last30 = entries.slice(-30);
    return {
      labels: last30.map(([date]) => {
        const d = new Date(date);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${mm}-${dd}`;
      }),
      data: last30.map(([, value]) => parseFloat(value) || 0),
    };
  }, [trendScore]);

  const chartData = {
    labels: trendChartData.labels,
    datasets: [
      {
        label: "Security Score",
        data: trendChartData.data,
        fill: true,
        borderColor: TREND_COLOR,
        backgroundColor: "rgba(25, 118, 210, 0.10)",
        pointRadius: 2,
        pointBackgroundColor: TREND_COLOR,
        tension: 0.35,
        borderWidth: 2,
      },
    ],
  };

  const chartRef = useRef();

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "white",
        titleColor: "#1a2233",
        bodyColor: "#1a2233",
        borderColor: "#e3eafc",
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return `Score: ${formatPercent(context.parsed.y)}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: { color: "#f0f0f0", drawBorder: false },
        ticks: {
          color: SUBTITLE_COLOR,
          font: { size: 10, weight: 500 },
          maxTicksLimit: 6,
          padding: 4,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        grid: { display: false },
        ticks: { display: false },
        min: 0,
        max: 100,
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { radius: 2 },
    },
  }), []);

  // Calculate severity data for account view
  const accountSeverityData = useMemo(() => {
    const result = {};
    if (scanObj?.auditChecks?.Checks) {
      for (const sev of SEVERITY_LABELS) {
        result[sev] = 0;
      }
      scanObj.auditChecks.Checks.forEach(check => {
        const sev = (check.Severity || '').toUpperCase();
        if (SEVERITY_LABELS.includes(sev) && check.Status === 'FAILED') {
          result[sev] += 1;
        }
      });
    }
    return result;
  }, [scanObj]);

  // Organization severity data
  const orgSeverityData = orgMetrics.severityData || {};

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Download file when URL is received
  React.useEffect(() => {
    console.log("🚀 ~ React.useEffect ~ downloadCspmReportMessage:", downloadCspmReportMessage)
    if (downloadCspmReportMessage) {
      
      const link = document.createElement("a");
      link.href = downloadCspmReportMessage;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        dispatch(clearAllCspmMessages());
      }, 500);
    }
  }, [downloadCspmReportMessage, dispatch]);

  // Show error snackbar
  React.useEffect(() => {
    if (downloadCspmReportError) {
      setSnackbar({
        open: true,
        message: downloadCspmReportError,
        severity: "error",
      });
      setTimeout(() => {
        dispatch(clearAllCspmMessages());
      }, 4000);
    }
  }, [downloadCspmReportError, dispatch]);

  const handleDownloadReport = () => {
    if (scanObj?.runId) {
      dispatch(downloadCspmReport({ runId: scanObj.runId }));
    }
  };

  // Calculate resources for both views
  const accountTotal = accountMetrics.uniqueResources || 0;
  const accountPassed = accountMetrics.passedResources || 0;
  const accountFailed = accountMetrics.failedResources || 0;
  const accountPassedPercent = accountTotal > 0 ? (accountPassed / accountTotal) * 100 : 0;

  const orgTotal = orgMetrics.uniqueResources || 0;
  const orgPassed = orgMetrics.passedResources || 0;
  const orgFailed = orgMetrics.failedResources || 0;
  const orgPassedPercent = orgTotal > 0 ? (orgPassed / orgTotal) * 100 : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, pb: 1.5 }}>
        {/* Responsive Layout - Organization (20%) | Account (80%) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 0,
            width: "100%",
            alignItems: "stretch",
          }}
        >
          {/* Organization View Section - 20% - Redesigned to Match Account View */}
          <Box
            sx={{
              flex: isMobile ? 1 : "0 0 20%",
              display: "flex",
              flexDirection: "column",
              pb: isMobile ? 2 : 0,
              borderBottom: isMobile ? "1px solid #e2e8f0" : "none",
              borderRight: isMobile ? "none" : "1px solid #e2e8f0",
              pr: isMobile ? 0 : 1.5,
              height: "100%",
            }}
          >
            {/* Organization View Title - Top Left */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, minHeight: 28 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#1e293b', 
                  fontSize: '0.875rem', 
                  fontWeight: 700,
                  letterSpacing: '0.02em'
                }}
              >
                Organization View
              </Typography>
            </Box>

            {orgLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1, height: "100%", justifyContent: 'space-between' }}>
                {/* Organization Security Score - Full Circle Chart with Dark Blue - Bigger to fill space */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', flex: '1 1 auto', minHeight: 0 }}>
                  <Box sx={{ position: 'relative', width: 110, height: 110, mb: 1 }}>
                    <svg width="110" height="110">
                      <circle cx="55" cy="55" r="48" stroke="#e2e8f0" strokeWidth="8" fill="none" />
                    </svg>
                    <svg width="110" height="110" style={{ position: 'absolute', top: 0, left: 0 }}>
                      <circle
                        cx="55"
                        cy="55"
                        r="48"
                        stroke="#0244a6"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={2 * Math.PI * 48}
                        strokeDashoffset={2 * Math.PI * 48 * (1 - (orgMetrics.percentChecks / 100))}
                        style={{ transition: 'stroke-dashoffset 0.6s', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                      />
                    </svg>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        px: 1,
                      }}
                    >
                      <CloudProviderMark
                        sx={{
                          mr: 0,
                          mb: 0.25,
                          "& .cloud-provider-mark--aws": { height: 14, width: 26 },
                          "& .cloud-provider-mark--azure": { height: 18, width: 18 },
                        }}
                      />
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.25rem',
                          lineHeight: 1.1,
                          color: '#1e293b',
                          textAlign: 'center',
                        }}
                      >
                        {formatPercent(orgMetrics.percentChecks)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>
                    Security Score
                  </Typography>
                </Box>

                {/* Resources Scanned - Organized - Same as Account View */}
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', flex: '0 0 auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 600 }}>
                      Resources Scanned
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e293b' }}>
                      {orgTotal}
                    </Typography>
                  </Box>
                  <Box sx={{ position: 'relative', height: 6, width: '100%', borderRadius: 3, overflow: 'hidden', bgcolor: '#fee2e2', mb: 0.6 }}>
                    <Box
                      sx={{
                        width: `${orgPassedPercent}%`,
                        height: '100%',
                        bgcolor: '#10b981',
                        transition: 'width 0.5s',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.7rem', fontWeight: 600 }}>
                      {orgPassed} Passed
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.7rem', fontWeight: 600 }}>
                      {orgFailed} Failed
                    </Typography>
                  </Box>
                </Box>

                {/* Failed by Severity - Organized - Same as Account View */}
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', flex: '0 0 auto', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', mb: 0.6, fontWeight: 600, display: 'block' }}>
                    Failed by Severity
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {SEVERITY_LABELS.map(sev => {
                      const count = orgSeverityData[sev] || 0;
                      if (count === 0) return null;
                      return (
                        <Chip
                          key={sev}
                          label={`${sev.charAt(0)}: ${count}`}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            backgroundColor: SEVERITY_BG_COLORS[sev] || '#f3f4f6',
                            color: SEVERITY_COLORS[sev] || '#6b7280',
                            borderRadius: '11px',
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Account View Section - 80% - Completely Redesigned */}
          {isLoading ? (
            <Box
              sx={{
                flex: isMobile ? 1 : "0 0 80%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                pl: isMobile ? 0 : 1.5,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : scanObj ? (
            <Box
              sx={{
                flex: isMobile ? 1 : "0 0 80%",
                display: "flex",
                flexDirection: "column",
                pl: isMobile ? 0 : 1.5,
                minWidth: 0,
                height: "100%",
              }}
            >
              {/* Account View Title - Top Left */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#1e293b', 
                    fontSize: '0.875rem', 
                    fontWeight: 700,
                    letterSpacing: '0.02em'
                  }}
                >
                  Account View
                </Typography>
                {scanObj?.runId && (
                  <Tooltip title="Download Report">
                    <IconButton
                      size="small"
                      onClick={handleDownloadReport}
                      disabled={isDownloadingCspmReport}
                      sx={{
                        width: 28,
                        height: 28,
                        color: "#64748b",
                        "&:hover": { 
                          backgroundColor: "rgba(100, 116, 139, 0.08)",
                          color: "#3b82f6"
                        },
                      }}
                    >
                      {isDownloadingCspmReport ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DownloadIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Organized Horizontal Layout - Aligned Heights */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 1.5,
                  alignItems: "stretch",
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {/* Left Section: Score + Metrics - Fixed Height to Match Trend */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, flex: isMobile ? "1" : "0 0 auto", minWidth: isMobile ? "100%" : 280, height: "100%" }}>
                  {/* Security Score */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', flex: '0 0 auto' }}>
                    <Box sx={{ position: 'relative', width: 90, height: 90, mb: 0.5 }}>
                      <svg width="90" height="90">
                        <circle cx="45" cy="45" r="38" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                      </svg>
                      <svg width="90" height="90" style={{ position: 'absolute', top: 0, left: 0 }}>
                        <circle
                          cx="45"
                          cy="45"
                          r="38"
                          stroke="#3b82f6"
                          strokeWidth="6"
                          fill="none"
                          strokeDasharray={2 * Math.PI * 38}
                          strokeDashoffset={2 * Math.PI * 38 * (1 - (accountMetrics.percentChecks / 100))}
                          style={{ transition: 'stroke-dashoffset 0.6s', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                        />
                      </svg>
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                          px: 0.75,
                        }}
                      >
                        <CloudProviderMark
                          sx={{
                            mr: 0,
                            mb: 0.2,
                            "& .cloud-provider-mark--aws": { height: 12, width: 22 },
                            "& .cloud-provider-mark--azure": { height: 16, width: 16 },
                          }}
                        />
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            lineHeight: 1.1,
                            color: '#1e293b',
                            textAlign: 'center',
                          }}
                        >
                          {formatPercent(accountMetrics.percentChecks)}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 500 }}>
                      Security Score
                    </Typography>
                  </Box>

                  {/* Checks Metrics - Organized Grid - Slightly Smaller */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75, flex: '0 0 auto' }}>
                    <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.4, fontWeight: 500 }}>
                        Total Checks
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                        {accountMetrics.executedChecks}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #c6f6d5' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.4, fontWeight: 500 }}>
                        Passed
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#10b981' }}>
                        {accountMetrics.passedChecks}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1.5, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.4, fontWeight: 500 }}>
                        Failed
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#ef4444' }}>
                        {accountMetrics.failedChecks}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Resources Scanned - Organized */}
                  <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', flex: '0 0 auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 600 }}>
                        Resources Scanned
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e293b' }}>
                        {accountTotal}
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'relative', height: 6, width: '100%', borderRadius: 3, overflow: 'hidden', bgcolor: '#fee2e2', mb: 0.6 }}>
                      <Box
                        sx={{
                          width: `${accountPassedPercent}%`,
                          height: '100%',
                          bgcolor: '#10b981',
                          transition: 'width 0.5s',
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.7rem', fontWeight: 600 }}>
                        {accountPassed} Passed
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.7rem', fontWeight: 600 }}>
                        {accountFailed} Failed
                      </Typography>
                    </Box>
                  </Box>

                  {/* Failed by Severity - Organized */}
                  <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', flex: '0 0 auto' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', mb: 0.6, fontWeight: 600, display: 'block' }}>
                      Failed by Severity
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {SEVERITY_LABELS.map(sev => {
                        const count = accountSeverityData[sev] || 0;
                        if (count === 0) return null;
                        return (
                          <Chip
                            key={sev}
                            label={`${sev.charAt(0)}: ${count}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: SEVERITY_BG_COLORS[sev] || '#f3f4f6',
                              color: SEVERITY_COLORS[sev] || '#6b7280',
                              borderRadius: '11px',
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>

                {/* Right Section: Trend Chart - Aligned Height with Left Section */}
                {trendChartData && trendChartData.labels && trendChartData.labels.length > 0 ? (
                  <Box 
                    sx={{ 
                        flex: 1, 
                        width: '100%', 
                        minWidth: 0, 
                        minHeight: 0,
                        overflow: 'hidden',
                        borderRadius: 1.5,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        p: 1.25,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', mb: 0.75, fontWeight: 600 }}>
                      Security Score Trend (30 Days)
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'visible' }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          overflow: "visible",
                          transition: "box-shadow 0.2s",
                          borderRadius: 2,
                          "&:hover": {
                            boxShadow: "0 4px 16px 0 rgba(25, 118, 210, 0.10)",
                          },
                          position: 'relative',
                        }}
                      >
                        <Line ref={chartRef} data={chartData} options={chartOptions} />
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 200, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                      No trend data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                flex: isMobile ? 1 : "0 0 80%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                pl: isMobile ? 0 : 1.5,
                p: 2,
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b', 
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textAlign: 'center'
                }}
              >
                No account data available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Snackbar for errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CSPMHeader; 